import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "error"]
  static values = {
    authenticateUrl: { type: String, default: "/sessions/passkey_options" },
    registerUrl: { type: String, default: "/passkeys/registrations/new" },
    registerCreateUrl: { type: String, default: "/passkeys/registrations" },
    loginUrl: { type: String, default: "/sessions" },
    dismissUrl: { type: String, default: "/passkeys/dismissal" },
    conditional: { type: Boolean, default: false },
    reloadOnRegister: { type: Boolean, default: false },
  }

  async connect() {
    if (this.conditionalValue) {
      this.startConditionalUI()
    } else {
      const supported = await this.isSupported()
      if (!supported) {
        this.element.hidden = true
      }
    }
  }

  disconnect() {
    this.abortController?.abort()
  }

  async isSupported() {
    if (
      !window.PublicKeyCredential ||
      !PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable
    ) {
      return false
    }
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  }

  async authenticate(event) {
    event.preventDefault()

    try {
      const optionsResponse = await fetch(this.authenticateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
      })
      const options = await optionsResponse.json()

      options.challenge = this.base64urlToBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map((cred) => ({
          ...cred,
          id: this.base64urlToBuffer(cred.id),
        }))
      }

      const credential = await navigator.credentials.get({
        publicKey: options,
      })

      this.submitCredential(credential)
    } catch (error) {
      if (error.name !== "NotAllowedError") {
        this.showError("Passkey authentication failed. Please try again.")
      }
    }
  }

  submitCredential(credential) {
    const credentialJSON = JSON.stringify({
      id: credential.id,
      rawId: this.bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        authenticatorData: this.bufferToBase64url(
          credential.response.authenticatorData
        ),
        clientDataJSON: this.bufferToBase64url(
          credential.response.clientDataJSON
        ),
        signature: this.bufferToBase64url(credential.response.signature),
        userHandle: credential.response.userHandle
          ? this.bufferToBase64url(credential.response.userHandle)
          : null,
      },
    })

    // Submit via form POST
    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.loginUrlValue

    const methodInput = document.createElement("input")
    methodInput.type = "hidden"
    methodInput.name = "_method"
    methodInput.value = "patch"
    form.appendChild(methodInput)

    const csrfInput = document.createElement("input")
    csrfInput.type = "hidden"
    csrfInput.name = "authenticity_token"
    csrfInput.value = this.csrfToken
    form.appendChild(csrfInput)

    const credInput = document.createElement("input")
    credInput.type = "hidden"
    credInput.name = "passkey_credential_json"
    credInput.value = credentialJSON
    form.appendChild(credInput)

    // Use a dummy ID for the route
    form.action = `${this.loginUrlValue}/current`

    document.body.appendChild(form)
    form.submit()
  }

  async startConditionalUI() {
    if (!PublicKeyCredential.isConditionalMediationAvailable) return
    const available =
      await PublicKeyCredential.isConditionalMediationAvailable()
    if (!available) return

    try {
      const optionsResponse = await fetch(this.authenticateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
      })
      const options = await optionsResponse.json()

      options.challenge = this.base64urlToBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map((cred) => ({
          ...cred,
          id: this.base64urlToBuffer(cred.id),
        }))
      }

      this.abortController = new AbortController()

      const credential = await navigator.credentials.get({
        publicKey: options,
        mediation: "conditional",
        signal: this.abortController.signal,
      })

      this.submitCredential(credential)
    } catch (error) {
      if (error.name === "AbortError" || error.name === "NotAllowedError")
        return
    }
  }

  async register(event) {
    event.preventDefault()

    try {
      const optionsResponse = await fetch(this.registerUrlValue, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
      })
      const options = await optionsResponse.json()

      options.challenge = this.base64urlToBuffer(options.challenge)
      options.user.id = this.base64urlToBuffer(options.user.id)
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map((cred) => ({
          ...cred,
          id: this.base64urlToBuffer(cred.id),
        }))
      }

      const credential = await navigator.credentials.create({
        publicKey: options,
      })

      const credentialJSON = JSON.stringify({
        id: credential.id,
        rawId: this.bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: this.bufferToBase64url(
            credential.response.attestationObject
          ),
          clientDataJSON: this.bufferToBase64url(
            credential.response.clientDataJSON
          ),
        },
      })

      const response = await fetch(this.registerCreateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ passkey_credential_json: credentialJSON }),
      })

      if (response.ok) {
        if (this.reloadOnRegisterValue) {
          window.location.reload()
        } else {
          this.element.remove()
        }
      } else {
        this.showError("Passkey registration failed. Please try again.")
      }
    } catch (error) {
      if (error.name !== "NotAllowedError") {
        this.showError("Passkey registration failed. Please try again.")
      }
    }
  }

  dismiss(event) {
    event.preventDefault()
    fetch(this.dismissUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfToken,
      },
    })
    this.element.remove()
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.hidden = false
    }
  }

  get csrfToken() {
    return document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")
  }

  // Base64url helpers for WebAuthn binary data
  base64urlToBuffer(base64url) {
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    )
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes.buffer
  }

  bufferToBase64url(buffer) {
    const bytes = new Uint8Array(buffer)
    let binary = ""
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "")
  }
}
